import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Segmented, Select, Switch, Typography } from 'antd';
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';

import { usePreferences } from '../../contexts/PreferencesContext';
import { PREFERENCE_DEFS } from '../../config/preferenceDefs';
import ThemePicker from './ThemePicker';
import ToggleRow from '../common/ToggleRow';
import { optionSearchFilter } from '../Input';
import { browserTimezone, timezoneOptions } from '../collections/ScheduleFields';
import { allCurrencyOptionGroups, currencySearchFilter } from '../../utils/money';
import { sameZone } from '../../utils/zonedClock';
import { readHomeTitle } from '../../utils/homeGreeting';

const { Text } = Typography;

const SAVED_FLASH_MS = 1500;

/**
 * The person's zone (Language & region): a searchable picker that can be
 * cleared back to "not set", and the device's zone one click away when it is
 * not the one chosen. Offered, never taken: nothing writes it for them.
 * "Is it the device's?" compares what the two names resolve to — two
 * spellings of one zone (US/Pacific, America/Los_Angeles) are the same zone.
 */
function TimezonePreference({ def, value, saving, onChange }) {
	const zone = typeof value === 'string' && value ? value : null;
	const device = browserTimezone();
	const options = useMemo(() => timezoneOptions(zone), [zone]);
	const offerDevice = Boolean(device) && !(zone && sameZone(zone, device));
	return (
		<>
			<Select
				showSearch
				allowClear
				filterOption={optionSearchFilter}
				value={zone || undefined}
				placeholder={def.unsetLabel}
				options={options}
				onChange={next => onChange(next || null)}
				loading={saving}
				style={{ width: '100%' }}
				aria-label={def.label}
			/>
			{offerDevice ? (
				<Button type='link' className='preference-row__suggestion' onClick={() => onChange(device)}>
					Use this device&rsquo;s time zone, {device}
				</Button>
			) : null}
		</>
	);
}

/** The person's currency (Language & region): every currency keepr knows, searchable, clearable. */
function CurrencyPreference({ def, value, saving, onChange }) {
	const options = useMemo(() => allCurrencyOptionGroups(), []);
	return (
		<Select
			showSearch
			allowClear
			filterOption={currencySearchFilter}
			value={typeof value === 'string' && value ? value : undefined}
			placeholder={def.unsetLabel}
			options={options}
			onChange={next => onChange(next || null)}
			loading={saving}
			style={{ width: '100%' }}
			aria-label={def.label}
		/>
	);
}

/**
 * A short plain-text preference (Home's title): saved when the field loses
 * focus or Enter is pressed, never on each keystroke; empty clears it. The
 * rule it is held to is the API's (utils/homeGreeting readHomeTitle).
 */
function TextPreference({ def, value, saving, onChange }) {
	const stored = typeof value === 'string' ? value : '';
	const [text, setText] = useState(stored);
	useEffect(() => { setText(stored); }, [stored]);
	const parsed = readHomeTitle(text);
	const commit = () => {
		if (parsed.error) return;
		const next = parsed.value;
		if ((next || '') !== stored) onChange(next);
	};
	return (
		<>
			<Input
				value={text}
				onChange={event => setText(event.target.value)}
				onBlur={commit}
				onPressEnter={commit}
				status={parsed.error ? 'error' : undefined}
				disabled={saving}
				allowClear
				aria-label={def.label}
				maxLength={def.maxLength ? def.maxLength + 20 : undefined}
			/>
			{parsed.error ? <Text type='danger'>{parsed.error}</Text> : null}
		</>
	);
}

function PreferenceControl({ def, value, saving, onChange }) {
	if (def.control === 'text') {
		return <TextPreference def={def} value={value} saving={saving} onChange={onChange} />;
	}
	if (def.control === 'timezone') {
		return <TimezonePreference def={def} value={value} saving={saving} onChange={onChange} />;
	}
	if (def.control === 'currency') {
		return <CurrencyPreference def={def} value={value} saving={saving} onChange={onChange} />;
	}
	if (def.control === 'theme') {
		return <ThemePicker options={def.options} value={value} onChange={onChange} />;
	}
	if (def.control === 'segmented') {
		return (
			<Segmented
				value={value}
				options={def.options}
				onChange={onChange}
				block
				aria-label={def.label}
			/>
		);
	}
	if (def.control === 'select') {
		return (
			<Select
				value={value}
				options={def.options}
				onChange={onChange}
				loading={saving}
				style={{ width: '100%' }}
				aria-label={def.label}
			/>
		);
	}
	return null;
}

/**
 * A yes/no preference's value: what is stored, else the registry's default —
 * never a hardcoded `!== false`, which would draw an opt-IN as already on.
 */
export function switchPreferenceChecked(def, value) {
	if (value === undefined || value === null) return def.default === true;
	return value === true;
}

function PreferenceStatus({ status }) {
	return (
		<span className='preference-row__status' aria-live='polite'>
			{status === 'saving' && <LoadingOutlined />}
			{status === 'saved' && (<><CheckOutlined /> Saved</>)}
		</span>
	);
}

function PreferenceRow({ def }) {
	const { prefs, setPref } = usePreferences();
	const [status, setStatus] = useState(null); // null | 'saving' | 'saved'
	const flashTimerRef = useRef(null);

	const handleChange = async (value) => {
		clearTimeout(flashTimerRef.current);
		setStatus('saving');
		const ok = await setPref(def.key, value);
		// Errors already toast from setPref; the row just stops indicating.
		setStatus(ok ? 'saved' : null);
		if (ok) {
			flashTimerRef.current = setTimeout(() => setStatus(null), SAVED_FLASH_MS);
		}
	};

	// A boolean SETTING is a ToggleRow everywhere in the product
	// (platform-standards §7 Toggles): the name and one line on the left, the
	// switch on the right.
	if (def.control === 'switch') {
		return (
			<ToggleRow
				className='preference-toggle'
				label={<>{def.label} <PreferenceStatus status={status} /></>}
				description={def.description}
			>
				<Switch
					checked={switchPreferenceChecked(def, prefs[def.key])}
					onChange={handleChange}
					loading={status === 'saving'}
					aria-label={def.label}
				/>
			</ToggleRow>
		);
	}

	return (
		<div className='preference-row'>
			<div className='preference-row__heading'>
				<Text strong>{def.label}</Text>
				<PreferenceStatus status={status} />
			</div>
			{def.description && (
				<Text type='secondary' className='preference-row__description'>{def.description}</Text>
			)}
			<PreferenceControl
				def={def}
				value={prefs[def.key]}
				saving={status === 'saving'}
				onChange={handleChange}
			/>
		</div>
	);
}

export default function PreferencesModal({ open, onClose }) {
	const groups = [];
	// A def that names a `surface` is edited on that page instead — the
	// notification masters live at the head of /account/notifications, beside
	// the list they govern. Rendering them here would put a row with no
	// control in the modal.
	PREFERENCE_DEFS.filter(def => !def.surface).forEach(def => {
		let group = groups.find(g => g.name === def.group);
		if (!group) {
			group = { name: def.group, defs: [] };
			groups.push(group);
		}
		group.defs.push(def);
	});

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			title='Preferences'
			width={480}
			centered
			destroyOnClose
			className='preferences-modal'
		>
			<Text type='secondary'>Changes are saved automatically.</Text>
			{groups.map(group => (
				<section key={group.name} className='preferences-modal__group'>
					<h3 className='preferences-modal__group-title'>{group.name}</h3>
					{group.defs.map(def => <PreferenceRow key={def.key} def={def} />)}
				</section>
			))}
		</Modal>
	);
}
